import clsx from 'clsx'
import { Button, Grid, Icon, NetworkIcon, Typography } from 'gu-ui-kit'
import { DoublePanelViewLayout } from 'layouts'
import formsMap from 'modules/Forms/Account/Create'
import formStyles from 'modules/Forms/common-form.module.scss'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Redirect, useHistory, useRouteMatch } from 'react-router'
import validator from 'utils/asyncValidator'
import styles from 'modules/Services/AccountCreate/account-register.module.scss'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import PropTypes from 'prop-types'
import SERVICES_API_V1 from 'api/services/v1'
import {
  ACCOUNT_CREATE_CONNECT_MANUAL_LOAD,
  CONFIRM_EXIT_FROM_CREATE_ACCOUNT,
  SET_NETWORK_DATA,
} from 'modules/Services/types'
import { useNetworksData } from 'modules/Services/hooks/useNetworksData'
import PageHeader from 'components/PageHeader'
import Dot from 'components/Dot'
import SidePanel from 'modules/Forms/Account/Create/subcomponents/SidePanel'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { notificationShow } from 'modules/Notifications/actions'

const AccountCreate = ({ multiLogins = false }) => {
  const pathGen = useGeneratePathI18n()
  const dispatch = useDispatch()
  const { t, i18n: { language } = {} } = useTranslation()
  const breakpoint = useBreakpoint()
  const history = useHistory()
  const {
    params: { i18n, networkType },
  } = useRouteMatch()

  const { networkId } = useNetworksData(networkType)
  const [sidePanelShown, setSidePanelShown] = useState(false)

  const {
    connectManualText: manual = null,
    bulk_account_creation_allowed,
    networks = [],
  } = useSelector((state) => ({
    ...state['services/account-create'],
    ...state['services/network'],
    ...state['services/list'],
  }))

  const formConfig = useMemo(() => formsMap?.[networkType], [networkType])

  useEffect(() => {
    if (!networkId) {
      return
    }

    if (multiLogins) {
      SERVICES_API_V1.getNetworkSettings({ networkId }).then(({ response: { data } }) => {
        if (!data?.bulk_account_creation_allowed) {
          return history.push(
            pathGen(ROUTES.SERVICES_NETWORK, {
              networkType,
            }),
          )
        }

        dispatch({
          type: SET_NETWORK_DATA,
          payload: {
            bulk_account_creation_allowed: !!data?.bulk_account_creation_allowed,
          },
        })
      })
    }

    dispatch({
      type: ACCOUNT_CREATE_CONNECT_MANUAL_LOAD,
      payload: {
        networkId,
        params: { lang: i18n },
      },
    })
  }, [networkId])

  if (!formConfig) {
    return <Redirect to={pathGen(ROUTES.SERVICES)} />
  }

  const FormComponent = useMemo(() => formConfig.component, [formConfig])

  const handleClose = useCallback(
    ({ confirm = true }) => {
      const { anyTouched = false, submitSucceeded = false } = JSON.parse(
        localStorage?.getItem('CreateAccountConfig') ?? {},
      )

      if (!confirm || !anyTouched || submitSucceeded) {
        history.push(
          pathGen(ROUTES.SERVICES_NETWORK, {
            networkType,
          }),
        )
        return
      }

      new Promise((resolve, reject) =>
        dispatch({
          type: CONFIRM_EXIT_FROM_CREATE_ACCOUNT,
          payload: {
            meta: { resolve, reject },
            title: t('services::forms.register_account.confirm_exit.title'),
            text: t('services::forms.register_account.confirm_exit.summary'),
            buttons: {
              cancel: t('services::forms.register_account.confirm_exit.buttons.cancel'),
              apply: t('services::forms.register_account.confirm_exit.buttons.apply'),
            },
          },
        }),
      ).then(() => {
        dispatch(
          notificationShow({
            type: 'success',
            message: 'common::notifications.data_success_add',
          }),
        )
        history.push(
          pathGen(ROUTES.SERVICES_NETWORK, {
            networkType,
          }),
        )
      })
    },
    [networkType, multiLogins],
  )

  const { homepage_alias = '' } = useMemo(
    () => networks?.find(({ type }) => type === networkType) ?? {},
    [networkType, networks],
  )

  return (
    <>
      <Helmet defer={false}>
        <title>
          {t('common::nav_menu.network_account_register')} {t(`services::networks.${networkType}`)}
        </title>
      </Helmet>

      <DoublePanelViewLayout onClose={handleClose}>
        <div className={clsx(formStyles['formContainer__inner'], styles['inner-container'])}>
          {networkType && (
            <FormComponent
              formHeader={
                <>
                  <Grid
                    alignItems={'start'}
                    wrap={false}
                    justifyContent={'start'}
                    gap={breakpoint.fbs ? 16 : 24}
                    className={'margin-bottom-48'}
                  >
                    <NetworkIcon
                      width={breakpoint.fbs ? 34 : 44}
                      height={breakpoint.fbs ? 34 : 44}
                      name={networkType}
                      rounded
                    />
                    <Grid
                      direction={'column'}
                      gap={8}
                    >
                      <PageHeader>{t('services::networks.' + networkType)}</PageHeader>
                      <Grid
                        alignItems={'center'}
                        wrap={false}
                      >
                        <Grid
                          i18nKey={'services::forms.register_account.about_network'}
                          component={'a'}
                          direction={'row'}
                          alignItems={'center'}
                          fluid={false}
                          gap={8}
                          wrap={false}
                          target={'_blank'}
                          href={`${language === 'ru' ? '' : '/en'}/networks/${homepage_alias}`}
                          rel="noopener noreferrer"
                        >
                          <Typography
                            variant={'Body-15'}
                            color={'Label-Secondary'}
                          >
                            {t('services::forms.register_account.about_network')}
                          </Typography>

                          <Icon
                            name={'follow-link'}
                            color={'Interface-Seventh'}
                            size={'s'}
                          />
                        </Grid>
                        {manual?.text && (
                          <>
                            <Dot
                              size={'s'}
                              color={'Interface-Fifth'}
                            />
                            <Grid
                              direction={'row'}
                              alignItems={'center'}
                              gap={8}
                              wrap={false}
                              fluid={false}
                              component={Button}
                              variant={'ghost'}
                              onClick={() => setSidePanelShown(true)}
                              link
                            >
                              <Typography
                                variant={'Body-15'}
                                color={'Label-GU-Primary'}
                              >
                                {t('services::forms.register_account.instruction')}
                              </Typography>
                              <Icon
                                name={'document'}
                                color={'Interface-GU-Primary'}
                                size={'s'}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>

                  <Typography
                    variant={'Subtitle-30'}
                    color="Label-Primary"
                    className={'margin-bottom-8'}
                    component={'h3'}
                  >
                    {t('services::forms.register_account.form_subtitle')}
                  </Typography>
                  <Typography
                    variant={'Body-15'}
                    color="Label-Secondary"
                    className={'margin-bottom-24'}
                    component={'p'}
                  >
                    {t('services::forms.register_account.form_summary')}
                  </Typography>
                </>
              }
              multiLogins={multiLogins && bulk_account_creation_allowed}
              touchOnChange
              networkType={networkType}
              networkId={networkId}
              onClose={handleClose}
              asyncValidate={validator(formConfig.schema(networkType))}
            />
          )}
        </div>
      </DoublePanelViewLayout>
      {manual && (
        <SidePanel
          shown={sidePanelShown}
          onClose={() => setSidePanelShown(false)}
        >
          <>
            <Typography
              className={'margin-bottom-32'}
              component={'h3'}
              variant={breakpoint.fbs ? 'Header-40' : 'Header-60'}
            >
              {manual.header}
            </Typography>
            <Typography
              variant={'Body-20'}
              component={'div'}
              dangerouslySetInnerHTML={{
                __html: manual.text,
              }}
            />
          </>
        </SidePanel>
      )}
    </>
  )
}

export default AccountCreate

AccountCreate.propTypes = {
  multiLogins: PropTypes.bool,
}
